import React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { alpha } from '@mui/material/styles';

// Custom Grid components
import GridContainer from '../components/common/GridContainer';
import GridItem from '../components/common/GridItem';
import SEO from '../components/common/SEO';
import { pageSEO, generateStructuredData } from '../utils/seo';

const AboutPage: React.FC = () => {
  const theme = useTheme();
  const seoData = pageSEO['/about'];
  const structuredData = generateStructuredData('/about');

  return (
    <>
      <SEO 
        pathname="/about" 
        {...seoData} 
        structuredData={structuredData}
      />
      <Box
        sx={{
        minHeight: '100vh',
        background: `
          linear-gradient(135deg, 
            ${alpha(theme.palette.primary.main, 0.05)} 0%, 
            ${alpha(theme.palette.secondary.main, 0.05)} 100%
          ),
          radial-gradient(
            circle at 50% 50%,
            ${alpha(theme.palette.primary.light, 0.1)} 0%,
            transparent 50%
          ),
          radial-gradient(
            circle at 0% 100%,
            ${alpha(theme.palette.secondary.light, 0.1)} 0%,
            transparent 50%
          ),
          radial-gradient(
            circle at 100% 0%,
            ${alpha(theme.palette.primary.light, 0.1)} 0%,
            transparent 50%
          )
        `,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(
              circle at 50% 50%,
              ${alpha(theme.palette.primary.main, 0.03)} 0%,
              transparent 70%
            )
          `,
          animation: 'pulse 8s ease-in-out infinite',
          '@keyframes pulse': {
            '0%': {
              transform: 'scale(1)',
              opacity: 1,
            },
            '50%': {
              transform: 'scale(1.2)',
              opacity: 0.8,
            },
            '100%': {
              transform: 'scale(1)',
              opacity: 1,
            },
          },
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            linear-gradient(
              45deg,
              transparent 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 52%,
              transparent 52%
            ),
            linear-gradient(
              -45deg,
              transparent 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 52%,
              transparent 52%
          )`,
          backgroundSize: '60px 60px',
          opacity: 0.5,
          animation: 'move 20s linear infinite',
          '@keyframes move': {
            '0%': {
              backgroundPosition: '0 0',
            },
            '100%': {
              backgroundPosition: '60px 60px',
            },
          },
        }}
      />
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          bgcolor: 'primary.main',
          color: 'white',
          py: { xs: 10, md: 12 },
          mb: 6,
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(to right, rgba(26, 43, 60, 0.9) 0%, rgba(26, 43, 60, 0.8) 50%, rgba(26, 43, 60, 0.7) 100%)`,
            zIndex: 0,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '100px',
            background: 'linear-gradient(to bottom, transparent, rgba(255, 255, 255, 0.1))',
            zIndex: 0,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h6"
              component="p"
              sx={{
                color: theme.palette.secondary.main,
                fontWeight: 600,
                mb: 1,
                letterSpacing: 1,
                textTransform: 'uppercase',
              }}
            >
              ABOUT US
            </Typography>
            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontFamily: 'Playfair Display, serif',
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                mb: 3,
                lineHeight: 1.2,
              }}
            >
            About Us
          </Typography>
            <Typography
              variant="h5"
              sx={{
                maxWidth: 800,
                opacity: 0.9,
                fontWeight: 400,
                lineHeight: 1.6,
              }}
            >
            The United Travel is the premier provider of corporate transportation services, delivering excellence in every ride.
          </Typography>
          </motion.div>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              mb: 6,
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
            }}
          >
            <Typography
              variant="h4"
              gutterBottom
              sx={{
                fontFamily: 'Playfair Display, serif',
                fontWeight: 600,
                color: 'primary.main',
              }}
            >
            Our Story
          </Typography>
          <Divider sx={{ mb: 3 }} />
            <Typography paragraph sx={{ fontSize: { xs: '1rem', md: '1.1rem' }, lineHeight: 1.8 }}>
            Founded in 2010, The United Travel began with a simple mission: to provide exceptional transportation services 
            that exceed client expectations. What started as a small fleet of luxury vehicles has grown into a 
            comprehensive transportation solution for businesses across the country.
          </Typography>
            <Typography paragraph sx={{ fontSize: { xs: '1rem', md: '1.1rem' }, lineHeight: 1.8 }}>
            Our founder, Jonathan Reed, recognized a gap in the market for truly premium corporate transportation that 
            prioritized professionalism, reliability, and attention to detail. Drawing from his background in hospitality 
            and business logistics, he assembled a team of experienced professionals committed to elevating the 
            transportation experience.
          </Typography>
            <Typography paragraph sx={{ fontSize: { xs: '1rem', md: '1.1rem' }, lineHeight: 1.8 }}>
            Today, The United Travel operates in major cities nationwide, serving Fortune 500 companies, financial institutions, 
            law firms, and various other corporate clients who demand nothing less than excellence.
          </Typography>
        </Paper>
        </motion.div>

        <GridContainer spacing={4} sx={{ mb: 6 }}>
          <GridItem xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              style={{ height: '100%' }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  height: '100%',
                  bgcolor: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 15px 40px rgba(0, 0, 0, 0.12)',
                    transform: 'translateY(-5px)',
                  },
                }}
              >
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{
                    color: 'primary.main',
                    fontFamily: 'Playfair Display, serif',
                    fontWeight: 600,
                  }}
                >
                Our Mission
              </Typography>
              <Divider sx={{ mb: 3 }} />
                <Typography sx={{ fontSize: { xs: '1rem', md: '1.1rem' }, lineHeight: 1.8 }}>
                To provide exceptional transportation experiences through reliability, professionalism, and personalized 
                service, ensuring our clients arrive at their destinations safely, comfortably, and on time, every time.
              </Typography>
            </Paper>
            </motion.div>
          </GridItem>
          <GridItem xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              style={{ height: '100%' }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  height: '100%',
                  bgcolor: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 15px 40px rgba(0, 0, 0, 0.12)',
                    transform: 'translateY(-5px)',
                  },
                }}
              >
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{
                    color: 'primary.main',
                    fontFamily: 'Playfair Display, serif',
                    fontWeight: 600,
                  }}
                >
                Our Values
              </Typography>
              <Divider sx={{ mb: 3 }} />
                <Box component="ul" sx={{ pl: 2, listStyle: 'none' }}>
                  <Typography component="li" paragraph sx={{ fontSize: { xs: '1rem', md: '1.1rem' }, lineHeight: 1.8 }}>
                  <strong>Excellence:</strong> We strive for excellence in every aspect of our service.
                </Typography>
                  <Typography component="li" paragraph sx={{ fontSize: { xs: '1rem', md: '1.1rem' }, lineHeight: 1.8 }}>
                  <strong>Reliability:</strong> We understand that punctuality and dependability are non-negotiable.
                </Typography>
                  <Typography component="li" paragraph sx={{ fontSize: { xs: '1rem', md: '1.1rem' }, lineHeight: 1.8 }}>
                  <strong>Professionalism:</strong> Our team maintains the highest standards of professionalism.
                </Typography>
                  <Typography component="li" sx={{ fontSize: { xs: '1rem', md: '1.1rem' }, lineHeight: 1.8 }}>
                  <strong>Client Focus:</strong> We tailor our services to meet the unique needs of each client.
                </Typography>
              </Box>
            </Paper>
            </motion.div>
          </GridItem>
        </GridContainer>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              mb: 6,
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
            }}
          >
            <Typography
              variant="h4"
              gutterBottom
              sx={{
                fontFamily: 'Playfair Display, serif',
                fontWeight: 600,
                color: 'primary.main',
              }}
            >
            Our Team
          </Typography>
          <Divider sx={{ mb: 3 }} />
            <Typography paragraph sx={{ fontSize: { xs: '1rem', md: '1.1rem' }, lineHeight: 1.8 }}>
            The United Travel team consists of experienced professionals committed to providing the highest level of service. 
            From our meticulously screened chauffeurs to our dedicated account managers, every team member plays a crucial role 
            in delivering the premium experience our clients expect.
          </Typography>
            <Typography paragraph sx={{ fontSize: { xs: '1rem', md: '1.1rem' }, lineHeight: 1.8 }}>
            Our chauffeurs undergo rigorous training in defensive driving, etiquette, and client service. Many have backgrounds 
            in executive protection or hospitality, bringing valuable skills to their role. Our administrative team includes 
            logistics experts, customer service specialists, and maintenance professionals who work together to ensure seamless 
            operations.
          </Typography>
            <Typography sx={{ fontSize: { xs: '1rem', md: '1.1rem' }, lineHeight: 1.8 }}>
            Leadership at The United Travel emphasizes continuous improvement, staying ahead of industry trends, and embracing 
            technologies that enhance the client experience while maintaining the personal touch that sets us apart.
          </Typography>
        </Paper>
        </motion.div>
      </Container>
    </Box>
    </>
  );
};

export default AboutPage; 